// ABOUTME: SPEC-53 — the black fade between the reel and a static collection stage: darken over
// ABOUTME: ~450ms, swap the stage while opaque, lighten. Chrome above it never moves (owner A1).
//
// Z-ORDER (the review's "implementation note"): the fade must cover the reel video AND the reel's
// stage overlays (point tags, CollectionOverlay, SpecSheetOverlay) but sit BELOW the chrome — the
// left icon rail (OverlayPortal z80), the wishlist/cart panels (inside that rail), the CC bar,
// mic pill and keyboard pill (ReelFooter/MobileFooter, OverlayPortal z65). Before this spec the
// stage overlays and the footer shared the same body-level portal z65, so no single z-index could
// sit between them. The smallest correct change was to SPLIT the portal by one prop: the
// Projectionist's own overlay portal now renders at `STAGE_OVERLAY_Z` (62) — see
// Projectionist/index.js — and this fade renders at `STAGE_FADE_Z` (63), a fixed body-level
// sibling. Stacking is then: video 20 < collection stage 21 < stage overlays 62 < fade 63 <
// footer 65 < rail 80. Nothing about the chrome changed, and the overlays keep their order among
// themselves. The Projectionist additionally hides its overlays while off stage, so nothing of the
// reel's chrome-in-the-picture survives under the collection.

import React, { useCallback, useEffect, useRef, useState } from 'react';

/** OQ-1 proposal, adopted: 450ms ease-in-out each way (the tags fade at TAG_FADE_MS = 400 under it). */
export const STAGE_FADE_MS = 450;
export const STAGE_FADE_Z = 63;

export const FadePhase = Object.freeze({ IDLE: 'idle', DARKENING: 'darkening', LIGHTENING: 'lightening' });

/**
 * Drives the fade. `begin(onSwap)` darkens, calls `onSwap` once fully opaque (the stage changes
 * under the black), then lightens. A `begin` while already darkening is absorbed — the pending swap
 * drains whatever the caller queued in the meantime, so two commands in one turn (enter + open)
 * get one fade. A `begin` while lightening restarts the darkening leg.
 *
 * @param {{ durationMs?: number }} [opts]
 * @returns {{ phase: string, phaseRef: {current: string}, begin: (onSwap: () => void) => void,
 *            opacity: number, active: boolean }}
 */
export function useStageTransition({ durationMs = STAGE_FADE_MS } = {}) {
  const [phase, setPhaseState] = useState(FadePhase.IDLE);
  const phaseRef = useRef(FadePhase.IDLE);
  const timerRef = useRef(null);
  const swapRef = useRef(null);

  const setPhase = useCallback((next) => { phaseRef.current = next; setPhaseState(next); }, []);

  const begin = useCallback((onSwap) => {
    swapRef.current = onSwap;
    if (phaseRef.current === FadePhase.DARKENING) return;
    clearTimeout(timerRef.current);
    setPhase(FadePhase.DARKENING);
    timerRef.current = setTimeout(() => {
      const swap = swapRef.current;
      swapRef.current = null;
      try { swap?.(); } catch (err) { console.error('[SPEC-53] stage swap threw:', err); }
      setPhase(FadePhase.LIGHTENING);
      timerRef.current = setTimeout(() => setPhase(FadePhase.IDLE), durationMs);
    }, durationMs);
  }, [durationMs, setPhase]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return {
    phase,
    phaseRef,
    begin,
    opacity: phase === FadePhase.DARKENING ? 1 : 0,
    active: phase !== FadePhase.IDLE,
  };
}

/**
 * The black itself. Always mounted (an element that was never painted at opacity 0 cannot ease
 * from it), pointer-transparent when idle, and a click shield while a fade is running so a tap
 * cannot land on a stage that is about to change — the chrome above it is unaffected either way.
 */
export function StageFade({ phase, durationMs = STAGE_FADE_MS, zIndex = STAGE_FADE_Z }) {
  const darkening = phase === FadePhase.DARKENING;
  const active = phase && phase !== FadePhase.IDLE;
  return (
    <div
      data-testid="stage-fade"
      data-phase={phase || FadePhase.IDLE}
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex,
        background: '#000',
        opacity: darkening ? 1 : 0,
        transition: `opacity ${durationMs}ms ease-in-out`,
        pointerEvents: active ? 'auto' : 'none',
      }}
    />
  );
}

export default useStageTransition;
